import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Modal, Button } from 'reactstrap';
import CourseExportModal from './CourseExportModal.jsx';
import ClipLoader from 'react-spinners/ClipLoader';
import moment from 'moment';
import { connect } from 'react-redux';
import './CourseDetailModal.css';

const BRAND_COLORS = {
  primary: '#0e1d3f'
};

export function CourseDetailModal(props){
  const {
    isOpen,
    toggle,
    loading,
    data = null,
    showStudents = false,
    onToggleShowStudents = () => {},
    onRefresh = () => {},
    onOpenClassroom = () => {},
    onLoadParticipants = () => {},
    classParticipants = {},
    classParticipantsLoading = {},
    onShowUserDetail = () => {},
    onBack,
    translate = {}
  } = props;

  // Auto-fetch quando recebemos apenas um objeto incompleto (ex: só id e name)
  const [autoData, setAutoData] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const baseData = autoData || data;

  useEffect(() => {
    if (!isOpen) return;
    if (!data || typeof data !== 'object') return;
    if (!data.id) return;
    // Se não temos classes ou lista vazia, tenta buscar no backend.
    if ((!data.classes || !Array.isArray(data.classes) || data.classes.length === 0) && !autoData && !autoLoading) {
      setAutoLoading(true);
      axios.get((window.backend || '') + '/course/' + data.id)
        .then(res => {
          if (res && res.data) setAutoData(res.data);
        })
        .catch(() => {})
        .finally(() => setAutoLoading(false));
    }
  }, [isOpen, data, autoData, autoLoading]);

  // lista de turmas do curso (preferindo autoData se carregado)
  const classesList = useMemo(() => {
    if (!baseData || !Array.isArray(baseData.classes)) return [];
    return [...baseData.classes];
  }, [baseData]);

  // filtro por Ano (openingDate)
  const [selectedYear, setSelectedYear] = useState('all');
  // filtro apenas turmas que realmente possuem alunos (reais, não derivados)
  const [onlyWithStudents, setOnlyWithStudents] = useState(false);
  // controla dropdown/collapse de units por turma
  const [unitsOpen, setUnitsOpen] = useState({});
  const toggleUnits = (id) => setUnitsOpen(prev => ({ ...prev, [id]: !prev[id] }));
  // controla dropdown de participantes por papel por turma
  const [participantsOpen, setParticipantsOpen] = useState({});
  const toggleParticipants = (classId, role) => {
    // se não tivermos participantes carregados, solicitar carregamento
    setParticipantsOpen(prev => {
      const current = (prev[classId] || {})[role];
      const next = !current;
      // se abrindo, solicitar carregamento dos participantes (não forçar abertura do painel principal)
      if (next && !classParticipants[classId]) {
        onLoadParticipants(classId);
      }
      return {
        ...prev,
        [classId]: { ...(prev[classId] || {}), [role]: next }
      };
    });
  };

  const [classDetailsOpen, setClassDetailsOpen] = useState({});
  // controla visibilidade da lista completa de participantes por turma (toggle do botão de participantes)
  const [participantsListOpen, setParticipantsListOpen] = useState({});
  const toggleClassDetails = (id) => {
    setClassDetailsOpen(prev => {
      const next = !prev[id];
      if (!next) {
        setParticipantsListOpen(p => ({ ...p, [id]: false }));
      }
      return { ...prev, [id]: next };
    });
  };
  // ref para manter posição de scroll e evitar "pular para cima" ao expandir
  const classroomListRef = useRef(null);
  const toggleParticipantsList = (classId) => {
    const container = classroomListRef.current;
    const prevScroll = container ? container.scrollTop : null;
    // se não tivermos participantes carregados, solicitar carregamento
    if (!classParticipants[classId]) {
      onLoadParticipants(classId);
    }
    const isOpening = !participantsListOpen[classId];
    setParticipantsListOpen(prev => ({ ...prev, [classId]: !prev[classId] }));
    if (isOpening) {
      setClassDetailsOpen(prev => ({ ...prev, [classId]: true }));
    }
    // após o re-render, restaurar scroll e focar card
    setTimeout(() => {
      if (container && prevScroll !== null) {
        container.scrollTop = prevScroll; // restaura posição anterior
      }
      const cardEl = document.getElementById(`class-card-${classId}`);
      if (cardEl) {
        cardEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 30);
  };

  // helper para normalizar papel a partir do usuário
  const normRole = (user) => {
    if (!user) return '';
    if (user._roleNorm) return user._roleNorm;
    const raw = (user.classRole !== undefined && user.classRole !== null) ? user.classRole : user.accessLevel;
    if (raw === undefined || raw === null) return '';
    const s = String(raw).toLowerCase();
    if (s === '6' || s === 'admin' || s === 'administrator') return 'admin';
    if (s === '3' || s === 'teacher' || s === 'instrutor' || s === 'instructor') return 'teacher';
    if (s === '4' || s === 'student' || s === 'aluno') return 'student';
    return '';
  };

  // ---------------------------------------------------------------------
  // computeClassStats
  // Retorna contagens derivadas da turma priorizando dados reais dos
  // participantes carregados (eliminando "fantasmas"). Quando a lista de
  // participantes ainda não foi carregada, faz um fallback usando os campos
  // agregados originais e a lógica de derivação existente.
  // Saída:
  // { students, teachers, admins, studentsActive, studentsInactive }
  const computeClassStats = useCallback((c) => {
    if (!c) {
      return {
        students: 0,
        teachers: 0,
        admins: 0,
        studentsActive: 0,
        studentsInactive: 0,
        teachersActive: 0,
        teachersInactive: 0,
        adminsActive: 0,
        adminsInactive: 0
      };
    }
    const list = classParticipants[c.id];
    if (Array.isArray(list) && list.length > 0) {
      let students = 0, teachers = 0, admins = 0;
      let studentsActive = 0, studentsInactive = 0;
      let teachersActive = 0, teachersInactive = 0;
      let adminsActive = 0, adminsInactive = 0;
      list.forEach(u => {
        const role = normRole(u);
        const blocked = Number(u.blocked || 0) === 1 || Number(u.blocked || 0) === -1 || u.blocked === true;
        if (role === 'student') {
          students += 1;
          if (blocked) studentsInactive += 1; else studentsActive += 1;
        } else if (role === 'teacher') {
          teachers += 1;
          if (blocked) teachersInactive += 1; else teachersActive += 1;
        } else if (role === 'admin') {
          admins += 1;
          if (blocked) adminsInactive += 1; else adminsActive += 1;
        }
      });
      return {
        students,
        teachers,
        admins,
        studentsActive,
        studentsInactive,
        teachersActive,
        teachersInactive,
        adminsActive,
        adminsInactive
      };
    }
    // -------- Fallback (sem participantes carregados) --------
    const teachers = Number(c.countTeachers || 0);
    const admins = Number(c.countAdmins || 0);
    const participants = Number(c.countParticipants || 0);
    let students;
    if (c.countStudents !== undefined && c.countStudents !== null) {
      students = Math.max(0, Number(c.countStudents) || 0);
    } else if (participants) {
      students = Math.max(0, participants - teachers - admins);
    } else {
      students = 0;
    }
    if (participants > 0 && participants === teachers + admins) students = 0;
    if (participants > 0 && (students + teachers + admins) > participants) {
      students = Math.max(0, participants - teachers - admins);
    }
    // distribuir inativos (apenas para estimar por papel)
    const inactive = Math.max(0, Number(c.countInactive || 0));
    const inactiveStudents = Math.min(inactive, students);
    let remainingInactive = Math.max(0, inactive - inactiveStudents);
    const inactiveTeachers = Math.min(remainingInactive, teachers);
    remainingInactive = Math.max(0, remainingInactive - inactiveTeachers);
    const inactiveAdmins = Math.min(remainingInactive, admins);

    const studentsInactive = inactiveStudents; // estimativa
    const studentsActive = Math.max(0, students - inactiveStudents);
    const teachersInactive = inactiveTeachers;
    const teachersActive = Math.max(0, teachers - inactiveTeachers);
    const adminsInactive = inactiveAdmins;
    const adminsActive = Math.max(0, admins - inactiveAdmins);

    return {
      students,
      teachers,
      admins,
      studentsActive,
      studentsInactive,
      teachersActive,
      teachersInactive,
      adminsActive,
      adminsInactive
    };
  }, [classParticipants, normRole]);

  const availableYears = useMemo(() => {
    const set = new Set();
    classesList.forEach(c => {
      if (c && c.openingDate) {
        const y = moment(c.openingDate).year();
        if (!isNaN(y)) set.add(String(y));
      }
    });
    return Array.from(set).sort((a,b)=> Number(b) - Number(a)); // descendente
  }, [classesList]);

  // total de turmas (após filtro de ano) que possuem alunos reais (>0) - usado para exibir número no chip
  const classesWithStudentsCount = useMemo(() => {
    const base = classesList.filter(c => {
      if (selectedYear === 'all') return true;
      if (!c || !c.openingDate) return false;
      return String(moment(c.openingDate).year()) === String(selectedYear);
    });
    return base.reduce((acc, c) => {
      const stats = computeClassStats(c);
      if (stats.students > 0) acc += 1;
      return acc;
    }, 0);
  }, [classesList, selectedYear, computeClassStats]);

  const filteredClasses = useMemo(() => {
    let arr = classesList.filter(c => {
      if (selectedYear === 'all') return true;
      if (!c || !c.openingDate) return false;
      return String(moment(c.openingDate).year()) === String(selectedYear);
    });
    if (onlyWithStudents) {
      arr = arr.filter(c => computeClassStats(c).students > 0);
    }
    return arr;
  }, [classesList, selectedYear, onlyWithStudents, computeClassStats]);

  const avatarSrc = (u) => {
    if (!u) return '/images/defaultAvatar.png';
    const p = u.photo ? String(u.photo).trim() : '';
    if (!p) return '/images/defaultAvatar.png';
    return p; 
  };

  // status textual simplificado
  const userStatus = (u) => {
    const blocked = Number(u?.blocked || 0);
    return blocked === 1 || blocked === -1 || u?.blocked === true ? (translate['bloqueado'] || 'Bloqueado') : (translate['nao_bloqueado'] || 'Não bloqueado');
  };

  const isClassActive = useCallback((c) => {
    if (!c) return false;
    const now = moment().startOf('day');
    const openOk = c.openingDate ? moment(c.openingDate).startOf('day').isSameOrBefore(now) : true;
    const closeOk = c.closingDate ? moment(c.closingDate).startOf('day').isSameOrAfter(now) : true;
    const withinDates = openOk && closeOk;
    if (c.countActive !== undefined && c.countActive !== null) {
      return Number(c.countActive) > 0 && withinDates;
    }
    return withinDates;
  }, []);

  // Estatísticas derivadas do filtro (turmas ativas e alunos não-bloqueados)
  const filteredStats = useMemo(() => {
    let activeClasses = 0;
    let nonBlockedStudents = 0;
    filteredClasses.forEach(c => {
      if (!c) return;
      const active = isClassActive(c);
      if (active) activeClasses += 1;
      const stats = computeClassStats(c);
      nonBlockedStudents += stats.studentsActive;
    });
    return { totalFiltered: filteredClasses.length, activeClasses, nonBlockedStudents };
  }, [filteredClasses, computeClassStats, isClassActive]);

  // Agregados considerando o filtro aplicado (preferindo participantes reais)
  const totals = useMemo(() => filteredClasses.reduce((acc, c) => {
    const stats = computeClassStats(c);
    acc.students += stats.students;
    acc.teachers += stats.teachers;
    acc.admins += stats.admins;
    acc.studentsActive += stats.studentsActive;
    acc.studentsInactive += stats.studentsInactive;
    // manter compatibilidade: active/inactive referem-se agora apenas a alunos
    acc.active += stats.studentsActive;
    acc.inactive += stats.studentsInactive;
    acc.units += Array.isArray(c.units) ? c.units.length : 0;
    return acc;
  }, { students: 0, teachers: 0, admins: 0, active: 0, inactive: 0, units: 0, studentsActive:0, studentsInactive:0 }), [filteredClasses, computeClassStats]);

  const [sidebarExpanded, setSidebarExpanded] = useState(false);

  const YearChips = () => (
    <div className="d-flex flex-wrap filter-chip-group" style={{gap: '6px'}}>
      <button onClick={()=>setSelectedYear('all')} className={`btn btn-xs ${selectedYear==='all'?'btn-primary':'btn-outline-primary'}`} style={{fontSize:'.7rem',padding:'0.25rem .55rem'}}> {translate['todos']||'Todos'} </button>
      {availableYears.map(y => (
        <button key={y} onClick={()=>setSelectedYear(y)} className={`btn btn-xs ${selectedYear===y?'btn-primary':'btn-outline-primary'}`} style={{fontSize:'.7rem',padding:'0.25rem .55rem'}}>{y}</button>
      ))}
      <button
        onClick={() => setOnlyWithStudents(o => !o)}
        className={`btn btn-xs ${onlyWithStudents ? 'btn-primary' : 'btn-outline-primary'}`}
        style={{fontSize:'.7rem',padding:'0.25rem .55rem'}}
        title={translate['filtrar_turmas_com_alunos'] || 'Filtrar apenas turmas que possuem alunos'}
      >
        <i className="fas fa-user-graduate mr-1" />{translate['com_alunos'] || 'Com alunos'} ({classesWithStudentsCount})
      </button>
    </div>
  );

  const MetricChip = ({icon, label, value, title}) => (
    <div className="metric-chip d-inline-flex align-items-center rounded mr-2 mb-2 shadow-sm"
         title={title||label}>
      <i className={`${icon} mr-1`} />
      <span className="text-uppercase" style={{fontWeight:600, letterSpacing:'.4px'}}>{label}:</span>&nbsp;<strong>{value}</strong>
    </div>
  );

  const ActiveRatioBar = ({active, total}) => {
    const pct = total>0? Math.round((active/total)*100):0;
    return (
      <div className="active-ratio-bar w-100" title={`${pct}% ${translate['ativos']||'ativos'}`}>
        <div className="fill" style={{width:`${pct}%`}} />
      </div>
    );
  };

  const ClassroomMetaBar = ({c}) => {
    const stats = computeClassStats(c);
    const totalParts = (stats.students + stats.teachers + stats.admins);
    return (
      <div className="mt-2 d-flex flex-wrap align-items-center" style={{gap:'4px'}}>
  <MetricChip icon="fas fa-user-graduate" label={translate['alunos']||'Alunos'} value={stats.students} />
  <MetricChip icon="fas fa-chalkboard-teacher" label={translate['instrutores']||'Instrutores'} value={stats.teachers} />
  <MetricChip icon="fas fa-user-shield" label={translate['admins']||'Admins'} value={stats.admins} />
  <MetricChip icon="fas fa-users" label={translate['participantes']||'Participantes'} value={totalParts} />
  <MetricChip icon="fas fa-check-circle" label={(translate['alunos']||'Alunos') + ' ' + (translate['nao_bloqueados_label']||'Não-bloqueados')} value={stats.studentsActive} />
      </div>
    );
  };

  const StyledCard = ({c, children}) => {
    const active = isClassActive(c);
    const stateClass = active ? 'is-active' : 'is-inactive';
    return (
      <div id={`class-card-${c.id}`} className={`card shadow-sm mb-3 position-relative classroom-card ${stateClass}`}>
        {children}
        {!active && (
          <span className="badge badge-outline-primary position-absolute" style={{top:8,right:8,fontSize:'.55rem',letterSpacing:'1px'}}>{translate['inativa']||'INATIVA'}</span>
        )}
        {active && (
          <span className="badge badge-primary position-absolute" style={{top:8,right:8,fontSize:'.55rem',letterSpacing:'1px'}}>{translate['ativa']||'ATIVA'}</span>
        )}
      </div>
    );
  };

  const SidebarStatRow = ({icon, label, value, tooltip}) => (
    <div className="d-flex justify-content-between align-items-center py-1 border-bottom" style={{fontSize:'.8rem'}} title={tooltip||label}>
      <div className="d-flex align-items-center text-muted">
        <i className={`${icon} text-primary mr-2`} style={{fontSize:'.75rem'}} />
        <span>{label}</span>
      </div>
      <strong className="text-primary" style={{fontSize:'.85rem'}}>{value}</strong>
    </div>
  );

  const totalParticipantsDerived = totals.studentsActive + totals.studentsInactive; // agora apenas alunos

  // Export modal state
  const [exportOpen, setExportOpen] = useState(false);
  const toggleExport = () => setExportOpen(o=>!o);

  // helper para garantir participantes antes de export (usado no modal)
  const ensureParticipants = async (classId) => {
    if(!classParticipants[classId]) {
      const ret = onLoadParticipants(classId);
      if(ret && ret.then) {
        await ret;
      } else {
        // aguardo leve para setState
        await new Promise(r=>setTimeout(r,50));
      }
    }
  };

  return (
    <Modal
      className="modal-dialog-centered"
      size="xl"
      isOpen={isOpen}
      toggle={toggle}
      style={{ maxWidth: '95%', maxHeight: '95vh' }}
      contentClassName="course-detail-modal"
    >
      <div className="modal-header d-flex align-items-center">
        <h5 className="modal-title mb-0 text-white ">{(translate['curso'] || 'Curso') + (baseData?.courseName ? (': ' + baseData.courseName) : (baseData?.name ? (': ' + baseData.name) : ''))}</h5>
        <div className="ml-auto d-flex align-items-center">
          <Button color="primary" size="sm" outline className="mr-2" onClick={toggleExport} title={translate['exportar_csv']||'Exportar CSV'}>
            <i className="fas fa-file-csv mr-1"></i>{translate['exportar']||'Exportar'}
          </Button>
          <button aria-label="Close" className="close" data-dismiss="modal" type="button" onClick={toggle}>
            <span aria-hidden={true}>×</span>
          </button>
        </div>
      </div>
  <div className="modal-body" style={{ maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' }}>
    {loading ? (
          <div className="text-center py-4">
            <ClipLoader size={40} color={BRAND_COLORS.primary} loading={true} />
      <p className="mt-3">{translate['carregando_informacoes_curso'] || 'Carregando informações do curso...'}</p>
          </div>
  ) : baseData ? (
          <div className="row">
            <div className="col-lg-8">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 className="text-uppercase text-muted mb-0">{translate['turmas'] || 'Turmas'}</h6>
                  <p className="mb-0" style={{ fontSize: '0.95rem' }}>
                    {filteredClasses.length} {translate['turma_encontrada_plural'] || 'turma(s) encontrada(s)'}
                    {selectedYear !== 'all' && <span className="text-muted"> ({translate['ano'] || 'Ano'}: {selectedYear})</span>}
                    {selectedYear !== 'all' && filteredClasses.length !== classesList.length && (
                      <span className="text-xs text-muted d-block">{translate['mostrando_filtradas_de_total'] || 'Mostrando filtradas de um total de'} {classesList.length}</span>
                    )}
                  </p>
                </div>
                <div className="text-right">
                  <YearChips />
                  <div className="mt-2"><Button size="sm" color="primary" outline onClick={onRefresh}><i className="fas fa-sync-alt mr-1"></i> {translate['atualizar'] || 'Atualizar'}</Button></div>
                </div>
              </div>

              <div ref={classroomListRef} className="classroom-list" style={{ maxHeight: '55vh', overflowY: 'auto', paddingRight: '10px' }}>
                {filteredClasses.length > 0 ? (
                  filteredClasses.map((classroom) => {
                    const stats = computeClassStats(classroom);
                    const totalParticipants = stats.students + stats.teachers + stats.admins;
                    const activeByRole = {
                      student: stats.studentsActive,
                      teacher: stats.teachersActive,
                      admin: stats.adminsActive
                    };
                    const inactiveByRole = {
                      student: stats.studentsInactive,
                      teacher: stats.teachersInactive,
                      admin: stats.adminsInactive
                    };
                    const participantsState = participantsOpen[classroom.id] || {};
                    const buildToggleBtnClass = (opened) => `btn btn-sm d-inline-flex align-items-center ${opened ? 'btn-primary text-white' : 'btn-outline-primary'}`;
                    const buildUnitsBtnClass = (opened) => `btn btn-sm d-inline-flex align-items-center ${opened ? 'btn-primary text-white' : 'btn-outline-primary'}`;
                    return (
                    <StyledCard key={classroom.id} c={classroom}>
                      <div className="card-body p-0">
                        <div className="d-flex p-3 align-items-center border-bottom">
                          <div className="flex-fill">
                            <h5 className="mb-1 text-primary"><span className="font-weight-bold">{classroom.name || `Turma #${classroom.id}`}</span></h5>
                            <div className="d-flex flex-wrap align-items-center text-sm text-muted">
                              <div className="mr-3"><i className="far fa-calendar-alt mr-1"></i> {moment(classroom.openingDate).format('DD/MM/YYYY')} → {moment(classroom.closingDate).format('DD/MM/YYYY')}</div>
                              {(classroom.companyName || classroom.company) && (
                                <div className="mr-3"><i className="far fa-building mr-1"></i> {classroom.companyName ? classroom.companyName : `Unidade ${classroom.company}`}</div>
                              )}
                            </div>
                            <ClassroomMetaBar c={classroom} />
                          </div>
                          <div className="ml-auto">
                              <div className="d-flex">
                              <Button color="primary" size="sm" outline={!classDetailsOpen[classroom.id]} className="mr-2" onClick={() => toggleClassDetails(classroom.id)}>
                                {classDetailsOpen[classroom.id] ? <><i className="fas fa-eye-slash mr-1"></i>{translate['ocultar_detalhes'] || 'Ocultar detalhes'}</> : <><i className="fas fa-eye mr-1"></i>{translate['ver_detalhes'] || 'Ver detalhes'}</>}
                              </Button>
                              <Button color="primary" outline size="sm" className="mr-2" onClick={() => onOpenClassroom(classroom.id)} title={translate['abrir_turma'] || 'Abrir turma'}><i className="fas fa-external-link-alt"></i> {translate['abrir'] || 'Abrir'}</Button>
                              <Button color="primary" size="sm" outline={!participantsListOpen[classroom.id]} onClick={() => toggleParticipantsList(classroom.id)} title={translate['ver_participantes'] || 'Ver participantes'}>
                                <i className={participantsListOpen[classroom.id] ? "fas fa-users-cog" : "fas fa-users"}></i> {totalParticipants}
                              </Button>
                            </div>
                          </div>
                        </div>
                        {classDetailsOpen[classroom.id] && (
                          <>
                        <div className="px-3 py-2 bg-lighter d-flex flex-wrap" style={{ fontSize: '0.95rem' }}>
                          <div className="mr-4 mb-1" style={{ fontSize: '0.95rem' }}>
                            <button
                              type="button"
                              className={buildToggleBtnClass(participantsState.student)}
                              onClick={() => toggleParticipants(classroom.id, 'student')}
                              aria-expanded={!!participantsState.student}
                              style={{ padding: '0.35rem 0.6rem' }}
                              aria-pressed={!!participantsState.student}
                            >
                              <i className="fas fa-user-graduate mr-1"></i> {stats.students} {translate['alunos'] || 'alunos'}
                            </button>
                          </div>
                          <div className="mr-4 mb-1" style={{ fontSize: '0.95rem' }}>
                            <button
                              type="button"
                              className={buildToggleBtnClass(participantsState.teacher)}
                              onClick={() => toggleParticipants(classroom.id, 'teacher')}
                              aria-expanded={!!participantsState.teacher}
                              style={{ padding: '0.35rem 0.6rem' }}
                              aria-pressed={!!participantsState.teacher}
                            >
                              <i className="fas fa-chalkboard-teacher mr-1"></i> {stats.teachers} {translate['instrutores'] || 'instrutores'}
                            </button>
                          </div>
                          <div className="mr-4 mb-1" style={{ fontSize: '0.95rem' }}>
                            <button
                              type="button"
                              className={buildToggleBtnClass(participantsState.admin)}
                              onClick={() => toggleParticipants(classroom.id, 'admin')}
                              aria-expanded={!!participantsState.admin}
                              style={{ padding: '0.35rem 0.6rem' }}
                              aria-pressed={!!participantsState.admin}
                            >
                              <i className="fas fa-user-shield mr-1"></i> {stats.admins} {translate['admins'] || 'admins'}
                            </button>
                          </div>
                          <div className="mr-4 mb-1" style={{ fontSize: '0.95rem' }}>
                            <button type="button" className={buildUnitsBtnClass(!!unitsOpen[classroom.id])} style={{ padding: '0.35rem 0.6rem' }} onClick={() => toggleUnits(classroom.id)} aria-expanded={!!unitsOpen[classroom.id]} aria-controls={`units-${classroom.id}`}>
                              <i className="fas fa-book mr-1"></i> {classroom.units?.length || 0} {translate['units'] || 'units'}
                            </button>
                          </div>
                          <div className="mr-4 mb-1 d-flex align-items-center" style={{ fontSize: '0.95rem' }}>
                            <span className="text-muted mr-2" style={{ fontSize: '0.9rem' }}>{translate['nao_bloqueados_label'] || 'Não-bloqueados:'}</span>
                            <button
                              type="button"
                              className={`${buildToggleBtnClass(participantsState.student)} mr-2`}
                              onClick={() => toggleParticipants(classroom.id, 'student')}
                              aria-expanded={!!participantsState.student}
                            >
                              <i className="fas fa-user-graduate mr-1" style={{ fontSize: '0.85rem' }}></i> <strong>{activeByRole.student}</strong>
                            </button>
                            <button
                              type="button"
                              className={`${buildToggleBtnClass(participantsState.teacher)} mr-2`}
                              onClick={() => toggleParticipants(classroom.id, 'teacher')}
                              aria-expanded={!!participantsState.teacher}
                            >
                              <i className="fas fa-chalkboard-teacher mr-1" style={{ fontSize: '0.85rem' }}></i> <strong>{activeByRole.teacher}</strong>
                            </button>
                            <button
                              type="button"
                              className={`${buildToggleBtnClass(participantsState.admin)} mr-2`}
                              onClick={() => toggleParticipants(classroom.id, 'admin')}
                              aria-expanded={!!participantsState.admin}
                            >
                              <i className="fas fa-user-shield mr-1" style={{ fontSize: '0.85rem' }}></i> <strong>{activeByRole.admin}</strong>
                            </button>
                          </div>

                          <div className="mb-1 d-flex align-items-center" style={{ fontSize: '0.95rem' }}>
                            <span className="text-muted mr-2" style={{ fontSize: '0.9rem' }}>{translate['bloqueados_label'] || 'Bloqueados:'}</span>
                            <button
                              type="button"
                              className={`${buildToggleBtnClass(participantsState.student_inactive)} mr-2`}
                              onClick={() => toggleParticipants(classroom.id, 'student_inactive')}
                              aria-expanded={!!participantsState.student_inactive}
                            >
                              <i className="fas fa-user-graduate mr-1" style={{ fontSize: '0.85rem' }}></i> <strong>{inactiveByRole.student}</strong>
                            </button>
                            <button
                              type="button"
                              className={`${buildToggleBtnClass(participantsState.teacher_inactive)} mr-2`}
                              onClick={() => toggleParticipants(classroom.id, 'teacher_inactive')}
                              aria-expanded={!!participantsState.teacher_inactive}
                            >
                              <i className="fas fa-chalkboard-teacher mr-1" style={{ fontSize: '0.85rem' }}></i> <strong>{inactiveByRole.teacher}</strong>
                            </button>
                            <button
                              type="button"
                              className={`${buildToggleBtnClass(participantsState.admin_inactive)} mr-2`}
                              onClick={() => toggleParticipants(classroom.id, 'admin_inactive')}
                              aria-expanded={!!participantsState.admin_inactive}
                            >
                              <i className="fas fa-user-shield mr-1" style={{ fontSize: '0.85rem' }}></i> <strong>{inactiveByRole.admin}</strong>
                            </button>
                          </div>

                          {/* Participant dropdowns per role */}
                          {classParticipants[classroom.id] && participantsOpen[classroom.id] && (
                            <div className="p-2" style={{ width: '100%' }}>
                              {['student', 'teacher', 'admin', 'student_inactive', 'teacher_inactive', 'admin_inactive'].map(roleKey => {
                                const open = participantsState[roleKey];
                                if (!open) return null;
                                const parts = classParticipants[classroom.id].filter(u => {
                                  const r = normRole(u);
                                  const isInactive = Number(u.blocked || 0) === 1 || Number(u.blocked || 0) === -1 || (u.blocked === true);
                                  if (roleKey === 'student') return r === 'student' && !isInactive;
                                  if (roleKey === 'teacher') return r === 'teacher' && !isInactive;
                                  if (roleKey === 'admin') return r === 'admin' && !isInactive;
                                  if (roleKey === 'student_inactive') return r === 'student' && isInactive;
                                  if (roleKey === 'teacher_inactive') return r === 'teacher' && isInactive;
                                  if (roleKey === 'admin_inactive') return r === 'admin' && isInactive;
                                  return false;
                                });
                                return (
                                  <div key={`${classroom.id}-${roleKey}`} className="bg-white rounded border mb-2 p-2">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                      <div className="font-weight-bold">{(() => {
                                        if (roleKey === 'student') return translate['estudante'] || 'Estudante';
                                        if (roleKey === 'student_inactive') return translate['estudante_bloqueados'] || 'Estudantes Bloqueados';
                                        return translate[roleKey] || roleKey.replace('_', ' ').toUpperCase();
                                      })()}</div>

                                    </div>
                                    {parts.length > 0 ? (
                                      <div className="row">
                                        {parts.map(u => {
                                          const statusBlocked = Number(u.blocked || 0) === 1 || Number(u.blocked || 0) === -1 || u.blocked === true;
                                          const statusLabel = userStatus(u);
                                          const statusBadgeClass = statusBlocked ? 'badge-outline-primary' : 'badge-primary';
                                          return (
                                            <div key={`p-${classroom.id}-${u.id}`} className="col-12 col-md-6 mb-1">
                                              <div className="d-flex align-items-center p-1">
                                                <div className="avatar-sm mr-2"><img src={avatarSrc(u)} alt="avatar" className="rounded-circle" style={{width: '28px', height: '28px', objectFit: 'cover'}}/></div>
                                                <div className="flex-fill text-truncate">
                                                  <div className="text-sm font-weight-bold text-truncate">{u.name} {u.lastName}</div>
                                                  <div className="text-xs text-muted text-truncate">{u.email}</div>
                                                  <div className="mt-1">
                                                      <span className={`badge badge-pill ${statusBadgeClass}`} style={{fontSize: '0.65rem'}}>{statusLabel}</span>
                                                  </div>
                                                </div>
                                                <div className="ml-2"><Button color="link" size="sm" className="p-0" onClick={() => onShowUserDetail(u)}><i className="fas fa-info-circle"></i></Button></div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <div className="text-muted">{translate['nenhum_participante_encontrado'] || 'Nenhum participante encontrado.'}</div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {classroom.units && classroom.units.length > 0 && unitsOpen[classroom.id] && (
                          <div id={`units-${classroom.id}`} className="p-3 border-top">
                            <h6 className="text-muted mb-2"><i className="fas fa-layer-group mr-1"></i> {translate['units'] || 'Units'}</h6>
                            <div className="units-list">{classroom.units.map(unit => (<div key={`unit-${classroom.id}-${unit.id}`} className="badge badge-outline-primary mr-2 mb-2 p-2">{unit.name || `Unit #${unit.id}`}</div>))}</div>
                          </div>
                        )}

                        {participantsListOpen[classroom.id] && classParticipants[classroom.id] && (
                          (classParticipantsLoading[classroom.id]) ? (
                            <div className="text-center py-3"><ClipLoader size={20} color={BRAND_COLORS.primary} loading={true} /></div>
                          ) : (
                            <div className="participants-list" style={{maxHeight: '300px', overflowY: 'auto'}}>
                              <div className="row">{classParticipants[classroom.id].map(user => {
                                const blocked = Number(user.blocked || 0) === 1 || Number(user.blocked || 0) === -1 || user.blocked === true;
                                const statusLabel = userStatus(user);
                                return (
                                  <div key={`part-${classroom.id}-${user.id}`} className="col-md-6 col-lg-4 mb-2">
                                    <div className="d-flex align-items-center p-2 bg-white rounded border">
                                      <div className="avatar-sm mr-2"><img src={avatarSrc(user)} alt="avatar" className="rounded-circle" style={{width: '32px', height: '32px', objectFit: 'cover'}}/></div>
                                      <div className="text-truncate">
                                        <div className="text-sm font-weight-bold text-truncate">
                                          {user.name} {user.lastName}
                                          {(() => {
                                            const norm = user._roleNorm || (() => {
                                              const raw = (user.classRole !== undefined && user.classRole !== null) ? user.classRole : user.accessLevel;
                                              if (raw === undefined || raw === null) return '';
                                              const s = String(raw).toLowerCase();
                                              if (s === '6' || s === 'admin' || s === 'administrator') return 'admin';
                                              if (s === '3' || s === 'teacher' || s === 'instrutor' || s === 'instructor') return 'teacher';
                                              if (s === '4' || s === 'student' || s === 'aluno') return 'student';
                                              return '';
                                            })();
                                            if (!norm) return null;
                                            const roleMap = {
                                              admin: { label: 'Admin', className: 'badge-primary', icon: 'fas fa-user-shield' },
                                              teacher: { label: 'Instrutor', className: 'badge-outline-primary', icon: 'fas fa-chalkboard-teacher' },
                                              student: { label: 'Aluno', className: 'badge-primary', icon: 'fas fa-user-graduate' }
                                            };
                                            const info = roleMap[norm] || { label: user._roleLabel || '', className: 'badge-outline-primary', icon: 'fas fa-user' };
                                            return (
                                              <span className={`ml-2 badge ${info.className} badge-pill d-inline-flex align-items-center`} title={`Papel: ${info.label}`} style={{ fontSize: '0.75rem', padding: '0.35rem 0.5rem' }}>
                                                <i className={`${info.icon} mr-1`} style={{ fontSize: '0.75rem' }} aria-hidden="true"></i>
                                                <span>{info.label}</span>
                                              </span>
                                            );
                                          })()}
                                        </div>
                                        <div className="text-xs text-muted text-truncate">{user.email}</div>
                                        <div className="mt-1"><span className={`badge badge-pill ${blocked ? 'badge-outline-primary' : 'badge-primary'}`} style={{fontSize: '0.65rem'}}>{statusLabel}</span></div>
                                      </div>
                                      <div className="ml-auto"><Button color="link" size="sm" className="p-0" onClick={() => onShowUserDetail(user)}><i className="fas fa-info-circle"></i></Button></div>
                                    </div>
                                  </div>
                                );
                              })}</div>

                              {classParticipants[classroom.id].length === 0 && (<div className="text-center py-2 text-muted">{translate['nenhum_participante_encontrado'] || 'Nenhum participante encontrado.'}</div>)}
                            </div>
                          )
                        )}
                          </>
                        )}

                      </div>
                    </StyledCard>
                  );
                  })
                ) : (
                  <div className="text-center p-5 bg-lighter rounded">
                    <div className="mb-3"><i className="fas fa-chalkboard text-muted" style={{fontSize: '3rem'}}></i></div>
                    <h5 className="text-muted">{translate['nenhuma_turma_encontrada_para_curso'] || 'Nenhuma turma encontrada para este curso'}</h5>
                  </div>
                )}
              </div>
            </div>

            <div className="col-lg-4">
              <div className="card shadow-sm h-100 position-relative sidebar-card">
                <div className="card-header p-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0" style={{fontWeight:600, letterSpacing:'.5px'}}><i className="fas fa-chart-pie mr-2"></i>{translate['resumo_do_curso']||'Resumo do Curso'}</h6>
                    <div className="d-flex align-items-center">
                      <Button color="primary" size="sm" outline={!sidebarExpanded} className="mr-2" onClick={() => setSidebarExpanded(o => !o)}>
                        {sidebarExpanded ? <><i className="fas fa-eye-slash mr-1"></i>{translate['ocultar_detalhes'] || 'Ocultar detalhes'}</> : <><i className="fas fa-eye mr-1"></i>{translate['ver_detalhes'] || 'Ver detalhes'}</>}
                      </Button>
                      <Button color="primary" size="sm" outline={!showStudents} onClick={onToggleShowStudents}>
                        {showStudents ? <><i className="fas fa-eye-slash mr-1"></i>{translate['ocultar']||'Ocultar'}</> : <><i className="fas fa-user-graduate mr-1"></i>{translate['alunos']||'Alunos'}</>}
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="card-body pb-2" style={{ fontSize: '.8rem' }}>
                  {selectedYear !== 'all' && (
                    <SidebarStatRow icon="fas fa-list" label={translate['total_sem_filtro']||'Total (sem filtro)'} value={classesList.length} />
                  )}
                  <SidebarStatRow icon="fas fa-layer-group" label={(translate['turmas']||'Turmas') + (selectedYear!=='all'?` (${translate['filtradas']||'Filtradas'})`:'' )} value={filteredClasses.length} />
                  <SidebarStatRow icon="fas fa-user-check" label={translate['turmas_com_alunos'] || 'Turmas com alunos'} value={classesWithStudentsCount} />
                  <SidebarStatRow icon="fas fa-play" label={`${translate['turmas_ativas']||'Turmas ativas'}${selectedYear!=='all'?` (${translate['filtradas']||'Filtradas'})`:''}`} value={filteredStats.activeClasses} />
                  <SidebarStatRow icon="fas fa-user-graduate" label={translate['alunos_unicos']||'Alunos únicos'} value={(baseData.students||[]).length} />
                  <SidebarStatRow icon="fas fa-check-circle" label={(translate['alunos']||'Alunos') + ' ' + (translate['nao_bloqueados_label']||'Não-bloqueados')} value={totals.studentsActive} />
                  <SidebarStatRow icon="fas fa-user-lock" label={(translate['alunos']||'Alunos') + ' ' + (translate['bloqueados_label']||'Bloqueados')} value={totals.studentsInactive} />
                  {sidebarExpanded && (
                    <>
                      <SidebarStatRow icon="fas fa-user-graduate" label={(translate['alunos']||'Alunos')+ ' (' + (translate['somatorio']||'somatório') + ')'} value={totals.students} />
                      <SidebarStatRow icon="fas fa-chalkboard-teacher" label={(translate['instrutores']||'Instrutores')+ ' (' + (translate['somatorio']||'somatório') + ')'} value={totals.teachers} />
                      <SidebarStatRow icon="fas fa-user-shield" label={(translate['admins']||'Admins')+ ' (' + (translate['somatorio']||'somatório') + ')'} value={totals.admins} />
                      <SidebarStatRow icon="fas fa-building" label={translate['units_totais']||'Units totais'} value={totals.units} />
                      <div className="mt-3">
                        <div className="mini-border-title mb-1">{translate['participacao_ativa']||'Participação Ativa'}</div>
                        <ActiveRatioBar active={totals.studentsActive} total={totalParticipantsDerived} />
                        <div className="text-right text-muted mt-1" style={{fontSize:'.65rem'}}>{totalParticipantsDerived>0? Math.round((totals.studentsActive/totalParticipantsDerived)*100):0}% {translate['alunos']||'Alunos'} {translate['nao_bloqueados_label']||'Não-bloqueados'}</div>
                      </div>
                    </>
                  )}
                  <div className="mt-3">
                    {showStudents ? (
                      <div className="students-list" style={{maxHeight:'32vh',overflowY:'auto'}}>
                        {(baseData.students||[]).length>0 ? (
                          (baseData.students||[]).map(student => {
                            const blocked = Number(student.blocked||0)===1 || Number(student.blocked||0)===-1 || student.blocked===true;
                            const statusLabel = userStatus(student);
                            return (
                              <div key={`sidebar-student-${student.id}`} className="d-flex align-items-center p-2 mb-2 bg-white rounded border">
                                <div className="avatar-sm mr-2"><img src={avatarSrc(student)} alt="avatar" className="rounded-circle" style={{width:28,height:28,objectFit:'cover'}}/></div>
                                <div className="flex-fill text-truncate">
                                  <div className="text-xs font-weight-bold text-truncate">{student.name} {student.lastName}</div>
                                  <div className="text-xxs text-muted text-truncate" style={{fontSize:'.6rem'}}>{student.email}</div>
                                  <span className={`badge badge-pill mt-1 ${blocked ? 'badge-outline-primary' : 'badge-primary'}`} style={{fontSize:'.5rem'}}>{statusLabel}</span>
                                </div>
                                <Button color="link" size="sm" className="p-0 ml-1" onClick={()=>onShowUserDetail(student)}><i className="fas fa-info-circle"></i></Button>
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-center text-muted py-3" style={{fontSize:'.7rem'}}>{translate['nenhum_aluno_matriculado_neste_curso']||'Nenhum aluno matriculado neste curso.'}</div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4 rounded students-placeholder">
                        <i className="fas fa-user-graduate text-muted mb-2" style={{fontSize:'1.4rem'}} />
                        <p className="mb-0 text-muted" style={{fontSize:'.65rem'}}>{translate['clique_em_ver_alunos_para_listar'] || 'Clique em "Alunos" para ver a lista completa.'}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>
        ) : (
          <div className="text-center text-muted py-5">
            {autoLoading ? (
              <>
                <ClipLoader size={30} color={BRAND_COLORS.primary} loading={true} />
                <h5 className="mt-3">{translate['carregando_detalhes_curso'] || 'Carregando detalhes do curso...'}</h5>
              </>
            ) : (
              <>
                <i className="fas fa-exclamation-circle fa-3x mb-3"></i>
                <h4>{translate['nenhum_dado_disponivel'] || 'Nenhum dado disponível'}</h4>
                <p>{translate['nao_foi_possivel_carregar_detalhes'] || 'Não foi possível carregar os detalhes deste curso.'}</p>
              </>
            )}
          </div>
        )}
      </div>

      <div className="modal-footer d-flex justify-content-between align-items-center w-100">
        <div>
          {onBack && (
            <Button
              color="primary"
              outline
              size="sm"
              className="d-inline-flex align-items-center footer-btn"
              onClick={onBack}
            >
              <i className="fas fa-arrow-left mr-1" />
              {translate['voltar'] || 'Voltar'}
            </Button>
          )}
        </div>
        <div>
          <Button
            color="primary"
            size="sm"
            className="d-inline-flex align-items-center footer-btn"
            onClick={toggle}
          >
            <i className="fas fa-times mr-1" />
            {translate['fechar'] || 'Fechar'}
          </Button>
        </div>
      </div>
      {exportOpen && (
        <CourseExportModal
          isOpen={exportOpen}
            toggle={toggleExport}
            classes={filteredClasses}
            baseData={baseData}
            translate={translate}
            onCollectParticipants={ensureParticipants}
            classParticipants={classParticipants}
        />
      )}
    </Modal>
  );
}

export default connect(state => ({ translate: state.translate }))(CourseDetailModal);
